import { useQuery } from '@tanstack/react-query';
import { type CSSProperties, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { OwnerEntity, UserRole } from '@bhops/shared';
import { dashboardApi } from '@/api/dashboard';
import { extractApiError } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import { IntegratedCalendar } from '@/components/IntegratedCalendar';
import { UpcomingEventsPanel } from '@/components/UpcomingEventsPanel';
import { ROBOT_STATUS_LABEL } from '@/lib/enum-labels';
import { ActionCard } from '@/components/ActionCard';
import { kstDateTime } from '@/lib/format-date';
import { SECTION_ACCENT } from '@/layout/section-accent';

interface Kpi {
  label: string;
  value: number | string;
  /** 값 뒤 단위(대/건/% 등). 시안 KpiStripItem 의 suffix. */
  suffix?: string;
  hint?: string;
  tone?: 'default' | 'warn' | 'danger';
}

const ENTITY_LABEL: Record<OwnerEntity, string> = {
  HONEYCOMB_ROBOTICS: '허니컴로보틱스',
  BEYOND_HONEYCOMB: '비욘드허니컴',
};

function nf(n: number | undefined): string {
  return typeof n === 'number' ? n.toLocaleString('ko-KR') : '—';
}

export function DashboardPage() {
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === UserRole.ADMIN;
  const navigate = useNavigate();

  // 이슈③ — ADMIN 법인(팀)별 전환 열람. '' = 전체(양 법인 합산). 비-ADMIN 은
  // 선택 UI 자체가 없고 서버가 자기 법인으로 강제하므로 이 state 무의미.
  const [entity, setEntity] = useState<'' | OwnerEntity>('');

  const kpisQ = useQuery({
    queryKey: ['dashboard', 'kpis', entity],
    queryFn: () => dashboardApi.kpis(entity || undefined),
    staleTime: 30_000,
    // 1-minute background refetch — dashboards benefit from staying fresh
    // even when the tab is open but idle.
    refetchInterval: 60_000,
  });

  const d = kpisQ.data;

  // v1.0.14 §1 — '만료 임박 (90일)' KPI 카드 제거 (사용자 피드백:
  // 실제 운영에서 액션으로 이어지지 않음). expiringSoon 데이터는 여전히
  // /contracts?status=ACTIVE&endingWithin=90 URL 필터로 접근 가능.
  const primary: Kpi[] = [
    { label: '이번 달 출하', value: nf(d?.deployments.thisMonth), suffix: '대' },
    {
      // PDF p.5: '활성 계약' → '계약 중' (운영 용어 통일).
      label: '계약 중',
      value: nf(d?.contracts.active),
      suffix: '건',
    },
    // PDF p.5: 'In_AS' → '정비 중'. RobotStatus.IN_AS 의 한글 표기와 일치
    // (RobotForm.tsx 의 status 옵션과 동일 단어 사용).
    { label: '정비 중', value: nf(d?.robots.byStatus.IN_AS), suffix: '대' },
    {
      // PDF p.5: '열린 리포트' → '접수된 리포트'. ReportsInboxPage 의 status 라벨
      // ('NEW' = 신규, 'TRIAGED' = 분류완료) 와 의미적으로 일치.
      label: '접수된 리포트',
      value: nf(d?.reports.open),
      suffix: '건',
      hint:
        (d?.reports.slaBreached ?? 0) > 0
          ? `SLA 초과 ${nf(d?.reports.slaBreached)} 건 포함`
          : undefined,
      tone: (d?.reports.slaBreached ?? 0) > 0 ? 'danger' : 'default',
    },
  ];

  // Plan v0.3.0 §B-3 (PPTX Slide 2) — RobotStatus values displayed in
  // Korean via ROBOT_STATUS_LABEL. The enum values stored in DB / sent
  // over the wire stay in canonical English (READY_TO_SHIP etc.); only
  // the rendered text is Korean.
  const fleet: Kpi[] = [
    { label: '전체 로봇', value: nf(d?.robots.total), suffix: '대' },
    {
      label: ROBOT_STATUS_LABEL.DEPLOYED,
      // round5 #19 — 이벤트/전시 임시 이동(kind≠CUSTOMER)을 뺀 실서비스 수.
      value: nf(d?.robots.operatingCount ?? d?.robots.byStatus.DEPLOYED),
      suffix: '대',
    },
    {
      label: ROBOT_STATUS_LABEL.READY_TO_SHIP,
      value: nf(d?.robots.byStatus.READY_TO_SHIP),
      suffix: '대',
    },
    {
      label: ROBOT_STATUS_LABEL.IN_PRODUCTION,
      value: nf(d?.robots.byStatus.IN_PRODUCTION),
      suffix: '대',
    },
  ];

  const entityRows = d ? Object.entries(d.byEntity) : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <section>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <h1 className="kuun-h2" style={{ margin: '0 0 4px' }}>
            대시보드
          </h1>
          {/* 2026-10-26 오너 — "여기서 팀 별 대시보드로 이동하는 버튼도 만들어야겠다". */}
          <nav aria-label="팀별 대시보드" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {TEAM_DASHBOARDS.map((t) => (
              <Link
                key={t.to}
                to={t.to}
                data-testid={`team-dashboard-link-${t.key}`}
                style={{
                  ...teamLink,
                  // 칸 전체를 팀 색으로 칠한다. 주황처럼 밝은 색은 흰 글씨가 묻혀 검정을 조금 섞는다.
                  background: `color-mix(in srgb, ${SECTION_ACCENT[t.key]} 78%, black)`,
                }}
              >
                {t.label} →
              </Link>
            ))}
          </nav>
          {/* 이슈③ — ADMIN 전용 법인(팀) 전환. 비-ADMIN 은 자기 법인 고정(선택 UI 없음). */}
          {isAdmin && (
            <select
              aria-label="법인 선택"
              value={entity}
              onChange={(e) => setEntity(e.target.value as '' | OwnerEntity)}
              style={{
                height: 32,
                padding: '0 10px',
                border: '1px solid var(--border-default)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--bg-surface)',
                color: 'var(--fg-default)',
                fontSize: 13,
                marginLeft: 'auto',
              }}
            >
              <option value="">두 법인 전체</option>
              {Object.entries(ENTITY_LABEL).map(([k, label]) => (
                <option key={k} value={k}>
                  {label}
                </option>
              ))}
            </select>
          )}
        </div>
        {/* 추가수정 2차(26-07-27) 정정3 — KPI 기준 시각을 KST + "(한국 시간)" 라벨로.
            콘솔 첫 화면의 기준 시각인데 toLocaleString 은 브라우저 로캘·시간대에
            따라 표기와 시각이 흔들렸고, 상세 화면 기준 표기(②A)의 라벨도 없었다. */}
        <div className="kuun-caption">
          {kpisQ.isFetching
            ? '불러오는 중…'
            : d
              ? `${kstDateTime(d.asOf, { label: true }) ?? '—'} 기준이며 1분마다 새로 고칩니다.${
                  isAdmin && entity ? ` ${ENTITY_LABEL[entity]}만 보고 있습니다.` : ''
                }`
              : '—'}
        </div>
      </section>

      {kpisQ.error && (
        <div
          style={{
            padding: 12,
            background: 'var(--bg-danger-subtle)',
            border: '1px solid var(--fg-danger)',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--fg-danger)',
            fontSize: 13,
          }}
        >
          {extractApiError(kpisQ.error)}
        </div>
      )}

      {/* §④ Phase 4 — 지금 해야 할 일 hero. 디자인 5원칙 ① 트리아지 우선.
          카드는 count > 0 일 때만 렌더 (실제 액션이 있을 때만 노출). */}
      {d && (
        <HeroActions
          slaBreached={d.reports.slaBreached ?? 0}
          openReports={d.reports.open ?? 0}
          inAs={d.robots.byStatus.IN_AS ?? 0}
          onNavigate={(p) => navigate(p)}
        />
      )}

      <section>
        <div className="kuun-label" style={{ marginBottom: 8 }}>
          주요 지표
        </div>
        <div style={kpiStrip}>
          {primary.map((k, i) => (
            <KpiStripItem key={k.label} k={k} first={i === 0} />
          ))}
        </div>
      </section>

      <section>
        <div className="kuun-label" style={{ marginBottom: 8 }}>
          로봇 상태 현황
        </div>
        <div style={kpiStrip}>
          {fleet.map((k, i) => (
            <KpiStripItem key={k.label} k={k} first={i === 0} />
          ))}
        </div>
      </section>

      {/* v1.0.14 §1 — ExpiringContractsWidget / TrendChartsSection
          제거 (사용자 피드백: 실 사용에서 noise). 90일 만료 임박 계약은
          /contracts?status=ACTIVE&endingWithin=90 URL 로, 트렌드는
          /reports (Phase 7 분석 페이지) 로 분리 예정. */}

      {/* Plan v0.3.0 §C-3 (PPTX Slide 3) — 3:2 split between calendar
          and upcoming events. Calendar runs in compact mode (smaller
          day cells, no legend, 2 chips/day) so it stays legible at
          ~60% width; UpcomingEventsPanel handles "what's coming up?"
          in chronological order on the right. */}
      <div className="kuun-stack-sm" style={calendarRow}>
        <IntegratedCalendar compact />
        <UpcomingEventsPanel />
      </div>

      {isAdmin && entityRows.length > 0 && (
        <section style={card}>
          <div className="kuun-label" style={{ marginBottom: 12 }}>
            법인별 집계
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                <th style={th}>법인</th>
                <th style={{ ...th, textAlign: 'right' }}>전체 로봇</th>
                <th style={{ ...th, textAlign: 'right' }}>운영 중 로봇</th>
              </tr>
            </thead>
            <tbody>
              {entityRows.map(([entity, counts]) => (
                <tr key={entity} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                  <td style={td}>{ENTITY_LABEL[entity as OwnerEntity] ?? entity}</td>
                  <td style={{ ...td, textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                    {nf(counts?.robots)}
                  </td>
                  <td style={{ ...td, textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                    {nf(counts?.deployed)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}

const TEAM_DASHBOARDS = [
  { key: 'business', to: '/business', label: '사업 대시보드' },
  { key: 'manufacturing', to: '/manufacturing', label: '제조 대시보드' },
  { key: 'field', to: '/field', label: '필드 대시보드' },
] as const;

const teamLink: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  height: 32,
  padding: '0 14px',
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  color: '#ffffff',
  fontSize: 13,
  fontWeight: 700,
  textDecoration: 'none',
  whiteSpace: 'nowrap',
};

// §④ Phase 4 — Dashboard hero "지금 해야 할 일" 3-card strip.
// 운영자가 dashboard 열자마자 "오늘 할 일" 을 한눈에 — 5원칙 중 ① 트리아지 우선.
//
// 카드 표시 정책 (시험-친화 — 모든 count = 0 이면 hero 자체가 사라짐):
//   • Card 1: SLA 초과 리포트 (slaBreached > 0) → /reports?sla=breached
//   • Card 2: 정비 중 로봇 (inAs > 0) → /manufacturing/robots?status=IN_AS
//   • Card 3: 접수된 리포트 (openReports > 0) → /reports
function HeroActions({
  slaBreached,
  openReports,
  inAs,
  onNavigate,
}: {
  slaBreached: number;
  openReports: number;
  inAs: number;
  onNavigate: (path: string) => void;
}): JSX.Element | null {
  const cards: JSX.Element[] = [];
  if (slaBreached > 0) {
    cards.push(
      <ActionCard
        key="sla"
        color="var(--severity-urgent)"
        label="SLA 초과 리포트"
        count={slaBreached}
        suffix="건을 바로 처리해 주세요"
        ctaLabel="접수 분류"
        ctaVariant="danger"
        onCta={() => onNavigate('/reports?sla=breached')}
      />,
    );
  }
  if (inAs > 0) {
    cards.push(
      <ActionCard
        key="inas"
        color="var(--robot-maintenance)"
        label="정비 중 로봇"
        count={inAs}
        suffix="대가 점검 중입니다"
        ctaLabel="정비 목록"
        ctaVariant="secondary"
        onCta={() => onNavigate('/manufacturing/robots?status=IN_AS')}
      />,
    );
  }
  if (openReports > 0) {
    cards.push(
      <ActionCard
        key="reports"
        color="var(--brand)"
        label="접수된 리포트"
        count={openReports}
        suffix="건이 검토를 기다립니다"
        ctaLabel="리포트 보기"
        ctaVariant="primary"
        onCta={() => onNavigate('/service-orders?view=reports')}
      />,
    );
  }
  if (cards.length === 0) return null;
  return (
    <section data-testid="dashboard-hero-actions">
      <div className="kuun-label" style={{ marginBottom: 8 }}>
        지금 해야 할 일
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${cards.length}, 1fr)`,
          gap: 12,
        }}
      >
        {cards}
      </div>
    </section>
  );
}

// 시안 windows.jsx KpiStripItem — 가로 strip 안의 KPI 한 칸.
// 라벨 + 큰 수(28/800, tabular-nums) + suffix 단위. 항목 사이는 좌측 1px 구분선.
function KpiStripItem({ k, first }: { k: Kpi; first: boolean }) {
  const toneColor: CSSProperties['color'] =
    k.tone === 'danger'
      ? 'var(--fg-danger)'
      : k.tone === 'warn'
        ? 'var(--dday-warning)'
        : 'var(--fg-default)';
  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        padding: '4px 18px',
        borderLeft: first ? 'none' : '1px solid var(--border-default)',
      }}
    >
      <div className="kuun-label" style={{ color: 'var(--fg-muted)' }}>
        {k.label}
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 7, marginTop: 8 }}>
        <span
          style={{
            fontSize: 28,
            fontWeight: 800,
            lineHeight: 1,
            letterSpacing: '-0.02em',
            fontVariantNumeric: 'tabular-nums',
            color: toneColor,
          }}
        >
          {k.value}
        </span>
        {k.suffix && (
          <span
            style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg-subtle)', marginBottom: 2 }}
          >
            {k.suffix}
          </span>
        )}
      </div>
      {k.hint && (
        <div className="kuun-caption" style={{ marginTop: 6, color: toneColor }}>
          {k.hint}
        </div>
      )}
    </div>
  );
}

// 시안 KPI strip — 단일 surface 카드 안에 항목을 가로로 배치(구분선으로 구획).
const kpiStrip: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-surface)',
  boxShadow: 'var(--shadow-sm)',
  padding: '16px 4px',
};

const calendarRow: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 2fr)',
  gap: 16,
  alignItems: 'start',
};

const card: CSSProperties = {
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-surface)',
  boxShadow: 'var(--shadow-md)',
  padding: 16,
};

const th: CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  fontWeight: 600,
  color: 'var(--fg-muted)',
  background: 'var(--bg-sunken)',
  borderBottom: '1px solid var(--border-subtle)',
};

const td: CSSProperties = { padding: '8px 10px' };
